import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Always use current origin for API calls (setupProxy.js handles /api proxying)
const API = `${window.location.origin}/api`;

// Настраиваем axios для отправки токена
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auto-refresh on 401: try /auth/refresh once, then retry the failed request.
// If refresh also fails → clean up tokens and redirect to /login (preserving
// the user's location so they can come back).
let _refreshing = null;

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const detail = error.response?.data?.detail || '';
        const isAuthFailure =
            status === 401 ||
            (status === 403 && /token|expired|invalid/i.test(detail));

        // Don't retry on the refresh/login endpoints themselves
        const url = original?.url || '';
        if (
            !isAuthFailure ||
            original?._retry ||
            url.includes('/auth/refresh') ||
            url.includes('/auth/login')
        ) {
            return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            // No way to refresh — bail out
            localStorage.removeItem('access_token');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        try {
            // De-duplicate concurrent 401s sharing the same refresh
            if (!_refreshing) {
                _refreshing = axios.post(`${API}/auth/refresh`, {
                    refresh_token: refreshToken,
                });
            }
            const { data } = await _refreshing;
            _refreshing = null;
            if (data?.access_token) {
                localStorage.setItem('access_token', data.access_token);
            }
            original._retry = true;
            // Update Authorization header for the retry
            original.headers = original.headers || {};
            const fresh = localStorage.getItem('access_token');
            if (fresh) original.headers.Authorization = `Bearer ${fresh}`;
            return axios(original);
        } catch (refreshErr) {
            _refreshing = null;
            // Refresh failed — full logout
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            return Promise.reject(refreshErr);
        }
    },
);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    // ============ Idle auto-logout ============
    // If backend returned idle_logout_minutes > 0, reset a timer on any user
    // activity (mousemove, keydown, click, scroll, touchstart, focus). When it
    // fires we call logout() and reload to /login.
    useEffect(() => {
        if (!user) return;
        const minutes = Number(user.idle_logout_minutes || 0);
        if (!minutes || minutes <= 0) return;

        const timeoutMs = minutes * 60 * 1000;
        let timerId;

        const fireLogout = async () => {
            try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            // Force reload so router picks up the unauth'd state
            window.location.href = '/login?reason=idle';
        };

        const reset = () => {
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(fireLogout, timeoutMs);
        };

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'focus'];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();

        return () => {
            if (timerId) clearTimeout(timerId);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [user]);

    const checkAuth = async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await axios.get(`${API}/auth/me`);
            setUser(data);
        } catch (e) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (login, password) => {
        const { data } = await axios.post(`${API}/auth/login`, { login, password });
        // Сохраняем токены в localStorage
        if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
        }
        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }
        const idle = data.security?.idle_logout_minutes ?? 0;
        setUser({ ...data.user, idle_logout_minutes: idle });
        return data.user;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch (e) {
            // ignore
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
    };

    const changePassword = async (oldPassword, newPassword) => {
        await axios.post(`${API}/auth/change-password`, {
            old_password: oldPassword,
            new_password: newPassword
        });
        // Update user state to clear password_change_required flag
        setUser(prev => ({ ...prev, password_change_required: false }));
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, changePassword }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
